import type { CSSProperties } from 'react'
import { CategoryChip } from '../../platform/primitives/CategoryChip'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { setCategoryLabel, type CategoryListStatus } from '../categories/setCategoryLabel'
import type { Txn } from './transactionWire'

/**
 * A row's category as a control: opens the picker for that row. Shared by the
 * grid and the phone cards so the two renderings name and wire it identically.
 * Pending rows get it too — the category stays editable on them (Q5).
 *
 * **The hit area is not the chip.** The chip is ~20px tall and its fill is the
 * button's own background, so growing the box would grow the visual. Instead
 * the floor (`useHitTarget('page')`: 33px, 44px on a phone — M0-R29) reaches
 * CSS as `--pill-hit`, and `::after` reserves at least that square around the
 * chip (`transactions.css`). An `::after`, not a `::before`: the hit-target
 * sweep measures a control as its own box unioned with an absolute `::after`.
 */
export function CategoryPill({
  t,
  name,
  status,
  onCategoryClick,
}: {
  t: Txn
  /** The category's name, or undefined when the row has none or this client has no name for its id. */
  name: string | undefined
  /** The categories list's state: a set id with no name is "Loading", "Unavailable" or, once loaded, unknown (R7). */
  status: CategoryListStatus
  onCategoryClick: (t: Txn, opener: HTMLButtonElement) => void
}) {
  const hit = useHitTarget('page')
  const set = setCategoryLabel(status, name)
  const label = t.categoryId === null ? 'Needs category' : set.name
  return (
    <button
      type="button"
      className={t.categoryId === null ? 'txn-chip txn-chip--warn txn-chip--button' : 'txn-pill-button'}
      aria-haspopup="dialog"
      aria-label={`Category for ${t.merchant}: ${label}`}
      style={{ '--pill-hit': `${hit}px` } as CSSProperties}
      onClick={(e) => onCategoryClick(t, e.currentTarget)}
    >
      {t.categoryId === null ? label : name === undefined ? set.shown : <CategoryChip name={name} size="chip" />}
      <span className="txn-pill-button__caret" aria-hidden="true">
        ▾
      </span>
    </button>
  )
}
